import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Chip } from '@mui/material';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import WifiIcon from '@mui/icons-material/Wifi';

/**
 * OfflineStatusBar - A global component that shows the current network status
 * 
 * This is a simplified online-only version that maintains UI compatibility
 * with the original offline-capable component.
 * 
 * In online-only mode, this component:
 * 1. Shows temporary alerts when connection state changes
 * 2. No longer shows pending operations (as there are none)
 * 3. Maintains the same UI elements for consistency
 */
const OfflineStatusBar = () => {
  const [isOffline, setIsOffline] = useState(false);
  const [showOnlineAlert, setShowOnlineAlert] = useState(false);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  // In online-only mode, we always have 0 pending requests
  // const [isSyncing, setIsSyncing] = useState(false); // Unused in online-only mode
  
  // Remove unused networkStatus import and handleSync function

  // Update network status when online status changes
  useEffect(() => {
    const checkNetworkStatus = () => {
      // In online-only mode, we just check navigator.onLine
      const isCurrentlyOffline = !navigator.onLine;
      console.log(`OfflineStatusBar: Device online status: ${navigator.onLine}`);
      
      if (isCurrentlyOffline !== isOffline) {
        setIsOffline(isCurrentlyOffline);
        
        if (isCurrentlyOffline) {
          setShowOfflineAlert(true);
        } else {
          setShowOnlineAlert(true);
          // Update last sync time when we come back online
          // setLastSyncTime(new Date());
        }
      }
    };
    
    // Check immediately and then every 10 seconds
    checkNetworkStatus();
    const intervalId = setInterval(checkNetworkStatus, 10000);
    
    return () => clearInterval(intervalId);
  }, [isOffline]);

  // Function to handle syncing (simplified in online-only mode)
  // const handleSync = async () => {
  //   if (isSyncing) return;
  //   
  //   setIsSyncing(true);
  //   try {
  //     console.log('[Online Mode] Manual sync requested');
  //     // In online-only mode, we don't need to do anything here
  //     // but we'll update the last sync time for UI consistency
  //     // setLastSyncTime(new Date());
  //     
  //     // Show quick feedback
  //     setShowOnlineAlert(true);
  //   } catch (error) {
  //     console.error('Error during sync operation:', error);
  //   } finally {
  //     setIsSyncing(false);
  //   }
  // };

  // Add listeners for online/offline events
  useEffect(() => {
    const handleOffline = () => {
      console.log('Device went offline');
      setIsOffline(true);
      setShowOfflineAlert(true);
    };
    
    const handleOnline = async () => {
      console.log('Device went online');
      setIsOffline(false);
      setShowOnlineAlert(true);
      setShowOfflineAlert(false);
      // setLastSyncTime(new Date());
    };
    
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);
  
  return (
    <>
      {/* Permanent Status Indicator - always visible when offline */}
      {isOffline && (
        <Chip
          icon={<CloudOffIcon />}
          label="Offline Mode"
          color="warning"
          variant="outlined"
          sx={{
            position: 'fixed',
            bottom: 72, // Positioned above the bottom navigation (56px + 16px margin)
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
        />
      )}
      
      {/* Temporary Online Alert - shows briefly when connection is restored */}
      <Snackbar
        open={showOnlineAlert}
        autoHideDuration={3000}
        onClose={() => setShowOnlineAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          icon={<WifiIcon />}
          severity="success" 
          variant="filled"
          onClose={() => setShowOnlineAlert(false)}
        >
          You're back online!
        </Alert>
      </Snackbar>
      
      {/* Temporary Offline Alert - shows briefly when connection is lost */}
      <Snackbar
        open={showOfflineAlert}
        autoHideDuration={5000}
        onClose={() => setShowOfflineAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          icon={<CloudOffIcon />}
          severity="warning"
        >
          You are offline. Some features may be unavailable until connection is restored.
        </Alert>
      </Snackbar>
    </>
  );
};

export default OfflineStatusBar;
